/**
 * RateBandMatrixTab — Admin > Rate bands by role (read-only; 0134).
 *
 * Founder ruling 2026-07-07: which rate bands each role may pick when
 * logging billable time becomes VISIBLE in the product. This renders the
 * tenant's EFFECTIVE matrix — one row per rate band, one column per role,
 * plus the two per-person switch columns from the People tab: "Does care
 * management" (0143) and "Does SPICE" (0134), which ADD bands on top of
 * whatever the role allows. DP4 §A: care-manager band eligibility lives on
 * the "Does care management" switch, not on a care-manager role column, so
 * those columns are the care-manager / SPICE story.
 *
 * Served from the SAME resolver the pickers + the server-side allowed-band
 * guard use (GET /v1/band-visibility → resolveBandVisibility) — the panel
 * can never drift from what the product enforces. READ-ONLY v1: a tenant's
 * matrix is changed via the config seam (tenants.config.billing.
 * band_visibility); the in-Admin editable grid is the explicit fast-follow.
 * manage_team-gated (mirrors the AI-tools views).
 *
 * Full-width: the matrix takes the content column as-is (founder feedback
 * 2026-07-10: no horizontal scrollbar at desktop widths — the old max-w cap
 * forced one). overflow-x-auto stays as the narrow-viewport safety valve.
 */

import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { bandMatrixApi, type BandMatrixResponse } from '@/lib/bandMatrix';
import { ErrorBanner } from './ErrorBanner';
import { SkeletonRow } from './Skeleton';
import { Chip } from '@/components/primitives/Chip';

export interface RateBandMatrixTabProps {
  tenantSlug: string;
}

function CellMark({ allowed }: { allowed: boolean }): ReactNode {
  return allowed ? (
    <span aria-label="allowed" className="text-affirm-deep font-semibold">
      ✓
    </span>
  ) : (
    <span aria-label="not allowed" className="text-ink-soft/50">
      –
    </span>
  );
}

export function RateBandMatrixTab({ tenantSlug }: RateBandMatrixTabProps): ReactNode {
  const query = useQuery<BandMatrixResponse, Error>({
    queryKey: ['admin-review', 'band-matrix', tenantSlug],
    queryFn: ({ signal }) => bandMatrixApi.get(tenantSlug, signal),
    staleTime: 5 * 60_000,
  });

  return (
    <div className="w-full" data-testid="rate-band-matrix-tab">
      <h2 className="font-display text-lg text-sage-800">Rate bands by role</h2>
      <p className="mt-1 text-sm text-ink-soft">
        Who can pick each rate band when logging time.
      </p>

      {query.isError && (
        <div className="mt-4">
          <ErrorBanner error={query.error} onRetry={() => void query.refetch()} what="the rate matrix" />
        </div>
      )}
      {query.isPending && (
        <div className="mt-6 space-y-2">
          <SkeletonRow />
          <SkeletonRow />
        </div>
      )}

      {query.data && (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm" data-testid="rate-band-matrix-table">
            <thead>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-sage-400">
                <th className="py-1.5 pr-3 font-semibold">Rate band</th>
                {query.data.roles.map((r) => (
                  <th key={r.role} className="py-1.5 px-2 font-semibold text-center whitespace-nowrap">
                    {r.label}
                  </th>
                ))}
                <th className="py-1.5 px-2 font-semibold text-center whitespace-nowrap">
                  Does care management
                </th>
                <th className="py-1.5 pl-2 font-semibold text-center whitespace-nowrap">
                  Does SPICE
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sage-800/[6%]">
              {query.data.rows.map((row) => (
                <tr key={row.band} data-testid={`band-matrix-row-${row.band}`}>
                  <td className="py-2 pr-3 text-ink whitespace-nowrap">
                    {row.label}
                    {row.off === true && (
                      <Chip variant="neutral" className="ml-2" data-testid={`rate-band-matrix-off-${row.band}`}>
                        Off
                      </Chip>
                    )}
                  </td>
                  {query.data.roles.map((r) => (
                    <td
                      key={r.role}
                      className="py-2 px-2 text-center"
                      data-testid={`band-matrix-cell-${row.band}-${r.role}`}
                    >
                      <CellMark allowed={row.cells[r.role] === true} />
                    </td>
                  ))}
                  <td
                    className="py-2 px-2 text-center"
                    data-testid={`band-matrix-cell-${row.band}-care-management-flag`}
                  >
                    <CellMark allowed={row.care_management} />
                  </td>
                  <td
                    className="py-2 pl-2 text-center"
                    data-testid={`band-matrix-cell-${row.band}-spice-flag`}
                  >
                    <CellMark allowed={row.spice} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-ink-soft">
            A person with two hats keeps both sets: someone marked both "Does
            care management" and "Does SPICE" can pick the care-management bands
            and the SPICE bands.
          </p>
        </div>
      )}
    </div>
  );
}
